import React, { useEffect, useState, useCallback } from "react";
import { Search, Receipt } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, LoadingBlock, EmptyState, formatDateTime, formatCurrency } from "@/components/Common";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";

const TYPES = ["deposit", "withdrawal_net", "withdrawal_commission", "withdrawal_fee", "withdrawal_gross", "refund", "service_charge", "service_payment", "admin_correction", "migration_opening", "deposit_correction"];

export default function Transactions() {
  const { lang } = useI18n();
  const L = (it, en, de) => (lang === "en" ? en : lang === "de" ? (de ?? it) : it);
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/portal/transactions", { params: { q: q || undefined, entry_type: type || undefined, status: status || undefined, page, page_size: 20 } });
    setItems(data.items); setTotal(data.total);
  }, [q, type, status, page]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="space-y-6">
      <h2 className="font-heading text-2xl font-medium text-white">{L("Transazioni", "Transactions", "Transaktionen")}</h2>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder={L("Cerca riferimento o motivo…", "Search reference or reason…", "Referenz oder Grund suchen…")} className="pl-9 bg-navy-paper border-white/10" data-testid="tx-search" />
        </div>
        <select value={type} onChange={(e) => { setPage(1); setType(e.target.value); }} className="h-10 rounded-md bg-navy-paper border border-white/10 px-3 text-sm text-white" data-testid="tx-type-filter">
          <option value="">{L("Tutti i tipi", "All types", "Alle Typen")}</option>
          {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} className="h-10 rounded-md bg-navy-paper border border-white/10 px-3 text-sm text-white" data-testid="tx-status-filter">
          <option value="">{L("Tutti gli stati", "All statuses", "Alle Status")}</option>
          <option value="confirmed">{L("Confermata", "Confirmed", "Bestätigt")}</option><option value="pending">{L("In attesa", "Pending", "Ausstehend")}</option>
        </select>
      </div>
      {items === null ? <LoadingBlock /> : items.length === 0 ? <EmptyState icon={Receipt} title={L("Nessuna transazione", "No transactions", "Keine Transaktionen")} /> : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-white/5 text-slate-400 text-xs uppercase tracking-wider">
              <tr><th className="text-left px-4 py-3">{L("Tipo", "Type", "Typ")}</th><th className="text-left px-4 py-3">{L("Descrizione", "Description", "Beschreibung")}</th><th className="text-left px-4 py-3">{L("Importo", "Amount", "Betrag")}</th><th className="text-left px-4 py-3">{L("Direzione", "Direction", "Richtung")}</th><th className="text-left px-4 py-3">{L("Stato", "Status", "Status")}</th><th className="text-left px-4 py-3">{L("Data", "Date", "Datum")}</th></tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`tx-${e.id}`}>
                  <td className="px-4 py-2.5 text-slate-300">{e.entry_type}</td>
                  <td className="px-4 py-2.5 text-slate-400 max-w-[260px]" data-testid={`tx-desc-${e.id}`}>{e.reason || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-white">{formatCurrency(e.amount, e.currency)}</td>
                  <td className="px-4 py-2.5"><span className={e.direction === "credit" ? "text-emerald-400" : e.direction === "debit" ? "text-red-400" : "text-slate-500"}>{e.direction}</span></td>
                  <td className="px-4 py-2.5 text-slate-400">{e.status}</td>
                  <td className="px-4 py-2.5 text-slate-500">{formatDateTime(e.effective_at || e.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      {total > 20 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">{total} {L("risultati", "results", "Ergebnisse")}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="border-white/20" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>{L("Precedente", "Previous", "Zurück")}</Button>
            <Button size="sm" variant="outline" className="border-white/20" disabled={page * 20 >= total} onClick={() => setPage((p) => p + 1)}>{L("Successiva", "Next", "Weiter")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
